/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: unified
 *
 * Lista de ventas en desktop — tabla ticket (referencia proto ventas-ux-v1):
 * Venta · Fecha · Canal · Cobro · Operador · Total (sortable) · estado.
 * El pill solo aparece cuando hay algo que inspeccionar (pendiente / QR sin
 * cobrar / factura en excepción / anulada). Mobile usa SaleListRowCard.
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ticketDeskTable } from '../../shared/ticket/ticketDeskTable.styles';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import type { SaleListRow } from '../domain/salesTypes';
import type { SaleSortKey, SaleSortDir } from '../application/useSalesListScreen';
import {
  formatSaleChannelLabel,
  formatSaleDisplayRef,
  formatSaleListTimestamp,
  formatSaleMoney,
  formatSaleListPaymentMethods,
  resolveSaleListActionablePill,
  spanishSaleStatusLabel,
} from '../application/salePresentation';

type Props = {
  rows: SaleListRow[];
  onOpenDetail: (row: SaleListRow) => void;
  paymentMethodNames?: Record<string, string>;
  diningTableNames?: Record<string, string>;
  cashierDisplayName?: (userId: string | null) => string | null;
  selfUserId?: string | null;
  sortKey?: SaleSortKey;
  sortDir?: SaleSortDir;
  onSortChange?: (key: SaleSortKey, dir: SaleSortDir) => void;
  embedded?: boolean;
};

function SortIndicator({ active, dir }: { active: boolean; dir: SaleSortDir }) {
  if (!active) return <Text style={styles.sortArrow}> ↕</Text>;
  return <Text style={styles.sortArrowActive}>{dir === 'asc' ? ' ↑' : ' ↓'}</Text>;
}

export function SalesTable({
  rows,
  onOpenDetail,
  paymentMethodNames = {},
  diningTableNames = {},
  cashierDisplayName,
  selfUserId = null,
  sortKey,
  sortDir = 'desc',
  onSortChange,
  embedded,
}: Props) {
  if (rows.length === 0) return null;

  const toggleSort = (key: SaleSortKey) => {
    if (!onSortChange) return;
    if (sortKey === key) {
      onSortChange(key, sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      onSortChange(key, 'desc');
    }
  };

  const sortHeader = (label: string, key: SaleSortKey, style: object, right = false) => (
    <TouchableOpacity
      style={style}
      onPress={() => toggleSort(key)}
      accessibilityRole="button"
      accessibilityLabel={`Ordenar por ${label}`}
    >
      <Text style={[ticketDeskTable.headerCell, right && ticketDeskTable.headerNum]}>
        {label}
        <SortIndicator active={sortKey === key} dir={sortDir} />
      </Text>
    </TouchableOpacity>
  );

  return (
    <View style={[ticketDeskTable.tableOuter, embedded && ticketDeskTable.tableOuterEmbedded]}>
      <View style={[ticketDeskTable.table, embedded && ticketDeskTable.tableEmbedded]}>
        <View style={[ticketDeskTable.row, ticketDeskTable.headerRow]}>
          <Text style={[ticketDeskTable.headerCell, styles.colRef]}>Venta</Text>
          {sortHeader('Fecha', 'created_at', styles.colDate)}
          <Text style={[ticketDeskTable.headerCell, styles.colChannel]}>Canal</Text>
          <Text style={[ticketDeskTable.headerCell, styles.colPay]}>Cobro</Text>
          {sortHeader('Operador', 'cashier_user_id', styles.colOperator)}
          {sortHeader('Total', 'total', styles.colTotal, true)}
          <Text style={[ticketDeskTable.headerCell, styles.colStatus]} />
        </View>
        {rows.map((row) => {
          const isVoided = row.status === 'voided';
          const refLabel = formatSaleDisplayRef({ saleId: row.id, counterNumber: row.counterNumber });
          const customerLabel = row.fiadoCustomerName ?? row.customerName ?? null;
          const operator = row.cashierUserId === selfUserId
            ? 'Vos'
            : cashierDisplayName?.(row.cashierUserId) ?? '—';
          const pill = resolveSaleListActionablePill(row);
          const tableName = row.diningTableId ? diningTableNames[row.diningTableId] : null;

          return (
            <TouchableOpacity
              key={row.id}
              style={[ticketDeskTable.row, isVoided && styles.voidedRow]}
              onPress={() => onOpenDetail(row)}
              accessibilityRole="button"
              accessibilityLabel={`${refLabel}, ${formatSaleMoney(row.total)}, ${formatSaleChannelLabel(row.channel)}, ${spanishSaleStatusLabel(row.status)}`}
              testID={`ventas.row.${row.id}`}
            >
              <Text
                style={[styles.refText, styles.colRef, isVoided && styles.voidedStruck]}
                numberOfLines={1}
              >
                {refLabel}
              </Text>
              <Text
                style={[ticketDeskTable.cell, styles.colDate, isVoided && styles.voidedText]}
                numberOfLines={1}
              >
                {formatSaleListTimestamp(row.createdAt)}
              </Text>
              <View style={styles.colChannel}>
                <Text
                  style={[ticketDeskTable.cell, isVoided && styles.voidedText]}
                  numberOfLines={1}
                >
                  {formatSaleChannelLabel(row.channel)}
                </Text>
                {tableName ? (
                  <Text style={ticketDeskTable.cellMuted} numberOfLines={1}>
                    Mesa: {tableName}
                  </Text>
                ) : null}
              </View>
              <View style={styles.colPay}>
                <Text
                  style={[ticketDeskTable.cell, isVoided && styles.voidedText]}
                  numberOfLines={1}
                >
                  {formatSaleListPaymentMethods(row.paymentMethodIds, paymentMethodNames)}
                </Text>
                {customerLabel ? (
                  <Text style={ticketDeskTable.cellMuted} numberOfLines={1}>
                    {customerLabel}
                  </Text>
                ) : null}
              </View>
              <Text
                style={[ticketDeskTable.cell, styles.colOperator, isVoided && styles.voidedText]}
                numberOfLines={1}
              >
                {operator}
              </Text>
              <View style={styles.colTotal}>
                <Text
                  style={[ticketDeskTable.moneyText, isVoided && styles.voidedAmount]}
                  numberOfLines={1}
                >
                  {formatSaleMoney(row.total)}
                </Text>
                {!isVoided && row.pendingAmount > 0 ? (
                  <Text style={[ticketDeskTable.cellMuted, styles.pendingSub]} numberOfLines={1}>
                    pendiente {formatSaleMoney(row.pendingAmount)}
                  </Text>
                ) : null}
              </View>
              <View style={styles.colStatus}>
                {pill ? (
                  <Text
                    style={[
                      ticketDeskTable.statusText,
                      pill.tone === 'bad' ? ticketDeskTable.statusBad : ticketDeskTable.statusWarn,
                    ]}
                    numberOfLines={1}
                  >
                    {pill.label}
                  </Text>
                ) : null}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  colRef: { flex: 0.7, minWidth: 64 },
  colDate: { flex: 1.1, minWidth: 110 },
  colChannel: { flex: 1, minWidth: 96 },
  colPay: { flex: 1.5, minWidth: 130 },
  colOperator: { flex: 0.9, minWidth: 84 },
  colTotal: { flex: 0.9, minWidth: 92, alignItems: 'flex-end' },
  colStatus: { width: 118, alignItems: 'flex-end' },
  refText: {
    fontSize: 13,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
  },
  voidedRow: {
    opacity: 0.65,
  },
  voidedStruck: {
    textDecorationLine: 'line-through',
    color: ticketColors.ink3,
  },
  voidedText: {
    color: ticketColors.ink3,
  },
  voidedAmount: {
    textDecorationLine: 'line-through',
    color: ticketColors.ink3,
  },
  pendingSub: {
    textAlign: 'right',
    color: ticketColors.warn,
  },
  sortArrow: {
    fontSize: 11,
    color: ticketColors.ink3,
  },
  sortArrowActive: {
    fontSize: 11,
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
});
